import { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

interface QueryParams {
	[key: string]: string;
}

interface UseQueryParamsHook {
	query: QueryParams;
	change: (newParams: QueryParams) => void;
}

/**
 * Provides reactive access to the current URL query parameters and a helper
 * to update them without triggering a full page reload.
 *
 * Query state is kept in sync with the URL via React Router's `useLocation`.
 *
 * @returns An object with:
 * - `query` — a key-value map of the current query parameters.
 * - `change(newParams)` — merges `newParams` into the current query string
 *   and pushes the updated URL to history.
 *
 * @example
 * const { query, change } = useQueryParams();
 * // Read: query.tab
 * // Write: change({ tab: 'settings' })
 */
const useQueryParams = (): UseQueryParamsHook => {
	const location = useLocation();
	const history = useHistory();
	const [query, setQueryParams] = useState<QueryParams>({});

	useEffect(() => {
		// Parse current query parameters from the URL
		const searchParams = new URLSearchParams(location.search);
		const params: QueryParams = {};

		for (const [key, value] of searchParams.entries()) {
			params[key] = value;
		}

		// Update state with current query parameters
		setQueryParams(params);
	}, [location.search]);

	const change = (newParams: QueryParams) => {
		// Create a new URLSearchParams object with the updated parameters
		const searchParams = new URLSearchParams(location.search);

		for (const key in newParams) {
			if (newParams[key]) {
				if (newParams[key]) {
					searchParams.set(key, newParams[key]);
				} else {
					searchParams.delete(key);
				}
			}
		}

		// Update the URL with the new query parameters
		history.push({ search: searchParams.toString() });
	};

	return { query, change };
};

/**
 * Parses query parameters from a full URL string into a key-value map.
 * Pure utility function — does not depend on React Router.
 *
 * @param url - The full URL string (e.g. `https://example.com/page?tab=1&view=list`).
 * @returns A record mapping each query parameter name to its string value.
 *
 * @example
 * getQueryParams('https://example.com?tab=settings&page=2');
 * // => { tab: 'settings', page: '2' }
 */
export function getQueryParams(url: string): Record<string, string> {
	const params: Record<string, string> = {};
	const queryString = url.split('?')[1];

	if (!queryString) {
		return params;
	}

	const queryParts = queryString.split('&');

	queryParts.forEach((part) => {
		const [key, value] = part.split('=');
		if (key) {
			params[decodeURIComponent(key)] = decodeURIComponent(value || '');
		}
	});

	return params;
}

export default useQueryParams;
